import React, { useState, useEffect, useRef } from "react";
import styles from "./LaserLinkSelector.module.css";

const LaserLinkSelector = ({ trackingLinks = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef(null);
  const count = trackingLinks.length;

  const handleSelect = (token) => {
    setIsOpen(false);
    window.location.href = `/tl-stats/${token}#/views`;
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setIsOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  // Filter tracking links by search query
  const filteredLinks = searchQuery.trim()
    ? trackingLinks.filter((tl) => {
        const q = searchQuery.toLowerCase();
        return (
          (tl.name || "").toLowerCase().includes(q) ||
          (tl.language_flag?.name || "").toLowerCase().includes(q) ||
          (tl.token || "").toLowerCase().includes(q)
        );
      })
    : trackingLinks;

  if (count === 0) return null;

  return (
    <div className={styles.selectorWrapper}>
      {/* Trigger */}
      <div
        className={`${styles.selectorTrigger} ${isOpen ? styles.selectorTriggerOpen : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${count} LaserLINX pointing to this KDF`}
      >
        <div className={styles.triggerContent}>
          <span className={styles.countBadge}>{count}</span>
          <span className={styles.triggerLabel}>
            LaserLINX {count === 1 ? "points" : "point"} to this KDF
          </span>
        </div>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}>
          &#9660;
        </span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <>
          <div className={styles.overlay} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdownPanel} role="listbox">
            {/* Search */}
            <div className={styles.searchWrapper}>
              <input
                ref={searchRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search LaserLINX..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search LaserLINX"
              />
            </div>

            {/* "All combined" option — keeps user on KDF stats */}
            <div
              className={styles.allOption}
              onClick={() => setIsOpen(false)}
              role="option"
              aria-selected="true"
            >
              <span className={styles.activeIndicator} />
              <div className={styles.allOptionInfo}>
                <div className={styles.allOptionTitle}>All combined</div>
                <div className={styles.allOptionMeta}>Currently displaying combined stats</div>
              </div>
            </div>

            <div className={styles.separator} />

            {/* Individual tracking links */}
            <div className={styles.listContainer}>
              {filteredLinks.length === 0 ? (
                <div className={styles.emptyState}>No matching LaserLINX found</div>
              ) : (
                filteredLinks.map((tl) => (
                  <div
                    key={tl.token || tl.id}
                    className={styles.tlItem}
                    onClick={() => handleSelect(tl.token)}
                    role="option"
                    aria-selected="false"
                    title={`Filter stats to ${tl.name}`}
                  >
                    {tl.language_flag?.class && (
                      <span
                        className={`${tl.language_flag.class} ${styles.tlFlag}`}
                      />
                    )}
                    <div className={styles.tlInfo}>
                      <div className={styles.tlName}>{tl.name}</div>
                      <div className={styles.tlMeta}>
                        {tl.language_flag?.name || ""}
                        {tl.created_at ? ` · ${tl.created_at}` : ""}
                      </div>
                    </div>
                    <span className={styles.tlArrow}>&#8594;</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LaserLinkSelector;
